import { ref, computed } from "vue";
import { isNull } from "@utils/var.check.ts";
import { useProcessingState } from "@modules/app/composables/processing-state.ts";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";
import type { CuePoints } from "@plugins/media/types.ts";

export interface MediaSettingsEmit {
  (e: "input", value: string): void;
  (e: "change", value: string): void;
  (e: "mediaLoaded", value: unknown): void;
  (e: "mediaPlayable", value: unknown): void;
  (e: "mediaReady", value: unknown): void;
  (e: "mediaRef", value: InstanceType<typeof MediaComponent>): void;
}

export interface UseMediaSettingEditViewProps {
  params?: { uuid?: string; mediaType?: string; strictMode?: boolean };
  mediaObjectMetadata?: { title?: string; description?: string };
  characterLimit?: Record<string, number>;
  error?: Record<string, string>;
  id?: string;
  showTranscriptInlineNotification?: boolean;
  cuePoints?: CuePoints;
  transcriptionFileVersion?: string;
  isViewingTranscript?: boolean;
  captionSrc?: string;

  processingId?: string;
}

export function useMediaSettingEditView(
  props: UseMediaSettingEditViewProps,
  emit: MediaSettingsEmit,
) {
  const isProcessingLocal = ref(false);

  const uuid = ref(props.params?.uuid ?? "");
  const mediaType = ref(props.params?.mediaType ?? "");
  const strictModeLocal = ref(!!props.params?.strictMode);

  const { isProcessing } = useProcessingState(props.processingId);

  // computed
  const isInputDisabled = computed(() => isProcessing.value || isProcessingLocal.value);

  const isTextToSpeech = computed(() => {
    const desc = props.mediaObjectMetadata?.description;
    return !!desc && !isNull(desc) && desc.includes("sls-tts");
  });

  const showRichTextDescription = computed(() => isTextToSpeech.value);

  // event handlers
  const handleTitleChange = (value: string) => emit("input", value);
  const handleDescriptionChange = (value: string) => emit("change", value);
  const handleMediaLoaded = (value: unknown) => emit("mediaLoaded", value);
  const handleMediaPlayable = (value: unknown) => emit("mediaPlayable", value);
  const handleMediaReady = (value?: unknown) => emit("mediaReady", value);

  return {
    // state
    isProcessingLocal,
    uuid,
    mediaType,
    strictModeLocal,

    // computed
    isInputDisabled,
    isTextToSpeech,
    showRichTextDescription,

    // handlers
    handleTitleChange,
    handleDescriptionChange,
    handleMediaLoaded,
    handleMediaPlayable,
    handleMediaReady,
  };
}
